import React, { useRef, type KeyboardEvent, type MouseEvent } from 'react';
import keycode from 'keycode';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import FocusRing from '@atlaskit/focus-ring';
import Heading from '@atlaskit/heading';
import ChevronDownIconOld from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIconOld from '@atlaskit/icon/glyph/chevron-right';

import Lozenge from '@atlaskit/lozenge';
import { cssMap } from '@atlaskit/css';
import { Box, xcss, Flex, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useCardListContext } from '../../../../services/card-list-context/index.tsx';
import { useSprintLifecycleContext } from '../../../../services/sprint-lifecycle-context/index.tsx';
import AddSprintDates from '../../add-sprint-dates/index.tsx';
import { COLUMN_LIMIT_TEST_ID, LEFT_SIDE_CONTAINER_TEST_ID } from '../../constants.tsx';
import { useFormatDate } from '../../hooks/use-format-date/index.tsx';
import messages from '../messages.tsx';
import { BACKLOG } from '../../../../model/constants.tsx';
import { CardListHeading } from './card-list-heading/index.tsx';

interface CardListAccordionButtonProps {
	onToggle: () => void;
}

export const CardListAccordionButton = ({ onToggle }: CardListAccordionButtonProps) => {
	const { formatMessage } = useIntl();
	const formatDate = useFormatDate();
	const {
		cardCountExcludingSubtasks,
		cardListId,
		isCollapsed,
		isFirstList,
		isMaxLimitReached,
		isMinLimitReached,
		maxColumnLimit,
		minColumnLimit,
		listName,
		sprintData,
		type,
		visibleCardAmount,
		isDisabled,
	} = useCardListContext();
	const { isAddSprintDatesButtonVisible } = useSprintLifecycleContext();

	const chevronRightIcon = (
		<ChevronRightIcon
			label={
				fg('jfp-a11y-remove-aria-lable-expand-icon')
					? ''
					: formatMessage(messages.expandAccessabilityLabel)
			}
			testId="software-backlog.card-list.card-list-header.card-list-accordion-button.expand-icon"
			color={token('color.icon')}
			LEGACY_fallbackIcon={ChevronRightIconOld}
			LEGACY_primaryColor={token('color.icon')}
			size="small"
		/>
	);

	const chevronDownIcon = (
		<ChevronDownIcon
			label={
				fg('jfp-a11y-remove-aria-lable-expand-icon')
					? ''
					: formatMessage(messages.collapseAccessabilityLabel)
			}
			testId="software-backlog.card-list.card-list-header.card-list-accordion-button.collapse-icon"
			color={token('color.icon')}
			LEGACY_fallbackIcon={ChevronDownIconOld}
			LEGACY_primaryColor={token('color.icon')}
			size="small"
		/>
	);

	const renderIcon = () => (isCollapsed ? chevronRightIcon : chevronDownIcon);

	const leftSideRef = useRef<HTMLInputElement | null>(null);

	const handleToggle = (event: MouseEvent<HTMLDivElement>) => {
		event.preventDefault();
		onToggle();
	};

	const handleHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.keyCode === keycode('enter') || event.keyCode === keycode('space')) {
			event.preventDefault();
			onToggle();
		}
	};

	const renderNumberOfVisibleCardsText = () => {
		if (expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)) {
			return cardCountExcludingSubtasks !== visibleCardAmount
				? formatMessage(messages.visibleCardAmountIssueTermRefresh, {
						totalCardAmount: cardCountExcludingSubtasks,
						cardAmount: visibleCardAmount,
					})
				: formatMessage(messages.cardAmountIssueTermRefresh, {
						cardAmount: cardCountExcludingSubtasks,
					});
		}

		return cardCountExcludingSubtasks !== visibleCardAmount
			? formatMessage(messages.visibleCardAmount, {
					totalCardAmount: cardCountExcludingSubtasks,
					cardAmount: visibleCardAmount,
				})
			: formatMessage(messages.cardAmount, {
					cardAmount: cardCountExcludingSubtasks,
				});
	};

	const renderAddSprintDatesButton = () => {
		const stopPropagation = (e: React.MouseEvent | React.KeyboardEvent) => e.stopPropagation();
		return (
			// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
			<Box
				onClick={stopPropagation}
				onKeyDown={stopPropagation}
				testId="software-backlog.card-list.card-list-header.card-list-accordion-button.add-sprint-date-button-container"
			>
				<AddSprintDates cardListId={Number(cardListId)} />
			</Box>
		);
	};

	const renderColumnLimits = () =>
		isFirstList && (
			<>
				{minColumnLimit ? (
					<Flex testId={COLUMN_LIMIT_TEST_ID}>
						<Lozenge
							appearance={isMinLimitReached ? 'moved' : 'default'}
							isBold={isMinLimitReached}
						>{`MIN ${minColumnLimit}`}</Lozenge>
					</Flex>
				) : null}
				{maxColumnLimit ? (
					<Flex testId={COLUMN_LIMIT_TEST_ID}>
						<Lozenge
							appearance={isMaxLimitReached ? 'removed' : 'default'}
							isBold={isMaxLimitReached}
						>{`MAX ${maxColumnLimit}`}</Lozenge>
					</Flex>
				) : null}
			</>
		);

	const containerId = `leftside-container-${cardListId}`;
	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box xcss={leftSideStyles} onClick={handleToggle}>
			<FocusRing>
				<Box
					xcss={cardListHeadingWrapper.pseudo}
					role="button"
					tabIndex={isDisabled && fg('tanuki_a11y_q4') ? -1 : 0}
					aria-controls={!isCollapsed ? `backlog-accordion-${cardListId}` : undefined}
					onKeyDown={handleHeaderKeyDown}
					aria-expanded={!isCollapsed}
					aria-labelledby={containerId}
					testId={LEFT_SIDE_CONTAINER_TEST_ID}
					ref={leftSideRef}
				/>
			</FocusRing>
			<Flex alignItems="center" justifyContent="center" xcss={iconStyles}>
				{renderIcon()}
			</Flex>
			<Box id={containerId}>
				<Flex alignItems="center" gap="space.100">
					{expValEquals('surface_origin_board_on_sprint_containers', 'cohort', 'test') ? (
						<CardListHeading
							listName={listName}
							boardName={sprintData?.originBoard?.boardName}
							rapidViewId={sprintData?.originBoard?.rapidViewId}
							isBacklog={type === BACKLOG}
						/>
					) : (
						<Heading as="h2" size="xsmall">
							{listName}
						</Heading>
					)}
					{sprintData && isAddSprintDatesButtonVisible && renderAddSprintDatesButton()}

					<span>
						{sprintData &&
							!isAddSprintDatesButtonVisible &&
							sprintData.startDate &&
							sprintData.endDate && (
								<Text size="medium" color="color.text.subtle">
									{`${formatDate(sprintData.startDate)} – ${formatDate(sprintData.endDate)}`}
								</Text>
							)}{' '}
						<Text size="medium" color="color.text.subtle">
							({renderNumberOfVisibleCardsText()})
						</Text>
					</span>

					{renderColumnLimits()}
				</Flex>
			</Box>
			<span data-testid={`${LEFT_SIDE_CONTAINER_TEST_ID}-${type}`}>
				<ScreenReaderText aria-live="polite" role="status">
					{listName} {renderNumberOfVisibleCardsText()}
				</ScreenReaderText>
			</span>
		</Box>
	);
};

const leftSideStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	cursor: 'pointer',
	flex: 1,
	gap: 'space.050',
	position: 'relative',
});

const cardListHeadingWrapper = cssMap({
	pseudo: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
	},
});

const iconStyles = xcss({ pointerEvents: 'none', width: '24px', height: '24px' });
